import React from 'react';
import { Box, BoxProps, Typography } from '@material-ui/core';
import ReturnButton from '../ReturnButton';

export interface BasicPageTemplateProps {
  pageTitle?: string;
  isSmallDevice: boolean;
  children?: JSX.Element | JSX.Element[];
  testId?: string;
  returnButtonText?: string;
  showReturnButton?: 'always' | 'small-device' | 'never';
  smallDeviceTopRight?: JSX.Element | JSX.Element[];
  containerSx?: BoxProps['sx'];
  submitButton?: React.ReactNode;
}

/**
 * Renders a minimal generic page template that helps drive consistency in terms of spacing, title size & return buttons
 *
 * @param pageTitle - (optional) main title for the page
 * @param isSmallDevice - small viewport or not
 * @param children - (optional) page contents
 * @param testId - the value for the 'data-testid' attribute
 * @param returnButtonText - (optional) useful when we want to show text other than 'Back'
 * @param showReturnButton - (optional) 'always' | 'small-device' | 'never'
 * @param smallDeviceTopRight - (optional) on  for rendering an element opposite the mobile back button (small devices ONLY)
 * @param submitButton - (optional) Usually the RJSF submit button to show on the top corner on all viewports
 */
const BasicPageTemplate: React.FC<BasicPageTemplateProps> = ({
  pageTitle,
  isSmallDevice,
  children,
  testId,
  returnButtonText,
  showReturnButton = 'small-device',
  smallDeviceTopRight,
  containerSx,
  submitButton,
}: BasicPageTemplateProps) => {
  const Title: JSX.Element = (
    <Typography variant={'h5'} component="h2">
      {pageTitle}
    </Typography>
  );

  const Return = !returnButtonText ? (
    <ReturnButton sx={{ mb: smallDeviceTopRight ? 0 : undefined }}>{returnButtonText}</ReturnButton>
  ) : (
    <ReturnButton sx={{ mb: 0 }} />
  );

  return (
    <Box
      data-testid={`basic-page-template${testId ? `-${testId}` : ''}`}
      padding={8}
      px={isSmallDevice ? 4 : undefined}
      sx={containerSx}
    >
      <Box display="flex" flexDirection="column">
        {(isSmallDevice || showReturnButton === 'always') && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={isSmallDevice ? 4 : 8}
            height="24px"
          >
            {showReturnButton === 'always' && Return}
            {showReturnButton === 'small-device' && isSmallDevice && Return}
            {!!smallDeviceTopRight && smallDeviceTopRight}
            {submitButton ? submitButton : <></>}
          </Box>
        )}

        {!isSmallDevice && pageTitle ? (
          <Box display="flex" justifyContent="space-between" width="100%" alignItems="center">
            {Title}
            {submitButton ? submitButton : <Box height={36.5} />}
          </Box>
        ) : (
          <></>
        )}
        {isSmallDevice && pageTitle && <Box mb={4}>{Title}</Box>}
        {children}
      </Box>
    </Box>
  );
};

export default BasicPageTemplate;
